import * as i0 from '@angular/core';
import { InjectionToken, EventEmitter, inject, forwardRef, Input, ViewChild, Output, Optional, Host, SkipSelf, Inject, ViewEncapsulation, Component, NgModule } from '@angular/core';
import * as i1 from '@angular/forms';
import { UntypedFormGroup, Validators, NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';
import { htmlId, formatUrlHttps } from '@engineering11/utility';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { IS_BROWSER, E11UtilModule } from '@engineering11/ui-lib/e11-ui-utils';
import * as i2 from '@angular/platform-browser';
import * as i3 from '@angular/common';
import { CommonModule } from '@angular/common';
import * as i4 from '@engineering11/ui-lib/e11-button';
import { E11ButtonModule } from '@engineering11/ui-lib/e11-button';
import * as i5 from '@engineering11/ui-lib/e11-input-label';
import { E11InputLabelModule } from '@engineering11/ui-lib/e11-input-label';
import * as i6 from '@engineering11/ui-lib/e11-icon';
import { E11IconModule } from '@engineering11/ui-lib/e11-icon';
import { E11TooltipModule } from '@engineering11/ui-lib/e11-tooltip';

const INPUT_CONFIG = new InjectionToken('INPUT_CONFIG');
const DEFAULT_CONFIG = {
    maxLengthByContext: {},
    maxLengthWarningPercent: 90,
};

const DEFAULT_MAX_LENGTH = 524288;
class E11InputComponent {
    set leadingIcon(value) {
        if (!value)
            return;
        if (value.startsWith('<svg')) {
            this._leadingIcon = this.sanitizer.bypassSecurityTrustHtml(value);
        }
        else {
            this._leadingIcon = this.sanitizer.bypassSecurityTrustHtml(`<span class="material-icons-outlined e11-text-skin-light">${value}</span>`);
        }
    }
    set trailingIcon(value) {
        if (!value)
            return;
        if (value.startsWith('<svg')) {
            this._trailingIcon = this.sanitizer.bypassSecurityTrustHtml(value);
        }
        else {
            this._trailingIcon = this.sanitizer.bypassSecurityTrustHtml(`<span class="material-icons-outlined e11-text-skin-light">${value}</span>`);
        }
    }
    constructor(controlContainer, el, sanitizer, cd, inputConfig) {
        this.controlContainer = controlContainer;
        this.el = el;
        this.sanitizer = sanitizer;
        this.cd = cd;
        this.inputConfig = inputConfig;
        this.containerClass = '';
        this.inputClass = '';
        this.hintText = null;
        this.label = '';
        /** Should be a valid HTML input type https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input#input_types */
        this.type = 'text'; // TODO: Statically type the valid options
        this.size = 'md';
        this.style = 'standard';
        this.placeholder = '';
        this.maxLength = DEFAULT_MAX_LENGTH;
        this.max = '';
        this.name = 'form-input';
        this.id = htmlId();
        this.iconLeft = '';
        this.iconLeftBorder = true;
        this.required = false;
        this.hasError = false;
        this.labelColor = ''; // primary - secondary
        this.borderGlow = '';
        this.checkIcon = false;
        this.clearIcon = false;
        this.autofocus = false;
        this.togglePassword = false;
        this.hasButton = false;
        this.buttonText = '';
        this.buttonIcon = '';
        this.buttonIconType = undefined; // 'filled' is default
        this.buttonIconSize = 'md-24'; // 'md-24' is default
        this.readonly = false;
        this.disabled = false;
        this.autocomplete = 'off';
        this.formControlName = ''; // This is the name of the individually created FormControl "nameFC"
        this.isRounded = false;
        this.parentForm = new UntypedFormGroup({});
        this.icon = '';
        this.iconType = '' || 'outlined' || 'two-tone' || 'sharp'; // 'filled' is default
        this.iconSize = 'md-24' || 'md-18' || 'md-36' || 'md-48'; // 'md-24' is default
        this.darkMode = false;
        this.context = null;
        this.enforceCharacterLimit = false;
        this.showCharacterLimitWarning = true;
        this.loading = false;
        this.change = new EventEmitter();
        this.focus = new EventEmitter();
        this.blur = new EventEmitter();
        this.destroy$ = new Subject();
        this.inputValue = '';
        this.showClearIcon = false;
        this.onChange = () => { };
        this.onTouch = () => { };
        this.borderGlowClass = '';
        this.reachingMaxLength = false;
        this.isFocused = false;
        this.isBrowser = inject(IS_BROWSER);
        if (!inputConfig)
            this.inputConfig = DEFAULT_CONFIG; // Enables the consuming application to not run .forRoot and still avoid runtime errors
    }
    ngOnInit() {
        this.initFormMethods();
        this.cd.detectChanges();
    }
    onValidate(formControl) {
        if (formControl.errors && (formControl.touched || formControl.dirty)) {
            this.hasError = true;
        }
        else {
            this.hasError = false;
        }
    }
    ngOnDestroy() {
        if (this.destroy$ && this.destroy$.next) {
            this.destroy$.next(true);
            this.destroy$.unsubscribe();
        }
    }
    onFocus() {
        this.focus.emit();
        this.isFocused = true;
    }
    onBlur() {
        this.blur.emit();
        this.isFocused = false;
    }
    onMouseEnter() {
        if (this.inputValue !== '' && this.clearIcon) {
            if (this.style != 'clean') {
                // Clean style removes certain icons
                this.showClearIcon = true;
            }
        }
    }
    onMouseLeave() {
        this.showClearIcon = false;
    }
    ngAfterViewInit() {
        if (this.autofocus) {
            this.setFocus();
        }
        if (this.maxLength) {
            if (this.inputElement) {
                this.inputElement.nativeElement['max-length'] = this.maxLength;
            }
        }
        this.initWheel();
        this.cd.detectChanges();
    }
    setFocus() {
        if (!this.isBrowser)
            return null;
        if (this.inputElement) {
            this.inputElement.nativeElement.focus();
        }
    }
    isInputEmpty() {
        return this.inputValue === '' || this.inputValue === null || this.inputValue === undefined;
    }
    onKeyup(value) {
        const previousValue = this.inputValue;
        this.inputValue = value;
        this.showClearIcon = value ? true : false;
        if (previousValue !== value) {
            this.onChange(value);
            this.change.emit(value);
        }
        if (this.style == 'clean') {
            this.showClearIcon = false;
        }
    }
    clearInput() {
        this.inputValue = '';
        this.onChange(this.inputValue);
    }
    writeValue(obj) {
        this.inputValue = obj ? obj : '';
    }
    registerOnChange(fn) {
        this.onChange = fn;
    }
    registerOnTouched(fn) {
        this.onTouch = fn;
    }
    handleOnChange(event) {
        let updatedValue = event.target.value.trim();
        if (this.type === 'url')
            updatedValue = formatUrlHttps(updatedValue);
        this.updateFormValue(updatedValue);
    }
    setShowPassword() {
        if (this.type == 'password')
            this.type = 'text';
        else
            this.type = 'password';
    }
    updateFormValue(value) {
        if (this.formControlName && this.parentForm.controls[this.formControlName]) {
            this.parentForm.controls[this.formControlName].setValue(value);
        }
    }
    initFormMethods() {
        if (this.context)
            this.maxLength = this.inputConfig.maxLengthByContext[this.context] ?? DEFAULT_MAX_LENGTH;
        if (this.borderGlow)
            this.borderGlowClass = 'border-glow-' + this.borderGlow;
        if (!this.formControlName || !this.parentForm)
            return;
        const formControl = this.parentForm.controls[this.formControlName];
        if (!formControl)
            return;
        if (this.parentForm.dirty && formControl?.errors)
            this.hasError = true;
        this.required = formControl.hasValidator(Validators.required);
        formControl?.valueChanges?.pipe(takeUntil(this.destroy$))?.subscribe(() => {
            if (this.maxLength && formControl.value?.length && this.inputConfig.maxLengthWarningPercent) {
                const percentageOfInput = Math.floor((formControl.value.length / this.maxLength) * 100);
                this.reachingMaxLength = this.showCharacterLimitWarning && percentageOfInput > this.inputConfig.maxLengthWarningPercent;
            }
            this.onValidate(formControl);
        });
        formControl.addValidators([Validators.maxLength(this.maxLength)]);
        formControl.updateValueAndValidity();
    }
    /** To prevent mousewheel from incrementing numeric inputs when desired */
    initWheel() {
        if (!this.isBrowser)
            return;
        const numericWheelStrategy = this.inputConfig.numericWheelStrategy;
        const el = document.getElementById(this.id);
        if (this.type !== 'number' || !numericWheelStrategy || !el)
            return;
        el.addEventListener('wheel', (e) => {
            if (numericWheelStrategy === 'blur')
                el.blur();
            if (numericWheelStrategy === 'stop')
                e.preventDefault();
        });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11InputComponent, deps: [{ token: i1.ControlContainer, host: true, optional: true, skipSelf: true }, { token: i0.ElementRef }, { token: i2.DomSanitizer }, { token: i0.ChangeDetectorRef }, { token: INPUT_CONFIG, optional: true }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.13", type: E11InputComponent, selector: "e11-input", inputs: { containerClass: "containerClass", inputClass: "inputClass", hintText: "hintText", label: "label", type: "type", size: "size", style: "style", placeholder: "placeholder", maxLength: "maxLength", max: "max", name: "name", id: "id", iconLeft: "iconLeft", iconLeftBorder: "iconLeftBorder", required: "required", hasError: "hasError", labelColor: "labelColor", borderGlow: "borderGlow", checkIcon: "checkIcon", clearIcon: "clearIcon", autofocus: "autofocus", togglePassword: "togglePassword", hasButton: "hasButton", buttonText: "buttonText", buttonIcon: "buttonIcon", buttonIconType: "buttonIconType", buttonIconSize: "buttonIconSize", readonly: "readonly", disabled: "disabled", disabledText: "disabledText", autocomplete: "autocomplete", formControlName: "formControlName", isRounded: "isRounded", parentForm: "parentForm", tooltipConfig: "tooltipConfig", icon: "icon", iconType: "iconType", iconSize: "iconSize", darkMode: "darkMode", context: "context", enforceCharacterLimit: "enforceCharacterLimit", showCharacterLimitWarning: "showCharacterLimitWarning", loading: "loading", leadingIcon: "leadingIcon", trailingIcon: "trailingIcon" }, outputs: { change: "change", focus: "focus", blur: "blur" }, providers: [
            {
                provide: NG_VALUE_ACCESSOR,
                useExisting: forwardRef(() => E11InputComponent),
                multi: true,
            },
        ], viewQueries: [{ propertyName: "inputElement", first: true, predicate: ["input"], descendants: true }], ngImport: i0, template: "<div class=\"e11-input-text {{ containerClass }}\" [formGroup]=\"parentForm\" (mouseenter)=\"onMouseEnter()\" (mouseleave)=\"onMouseLeave()\">\n  <e11-input-label\n    [for]=\"name\"\n    [label]=\"label\"\n    [required]=\"required\"\n    [disabled]=\"disabled\"\n    [hasError]=\"hasError\"\n    [darkMode]=\"darkMode\"\n    [tooltipConfig]=\"tooltipConfig\"\n  ></e11-input-label>\n  <div class=\"e11-relative e11-flex e11-items-center\">\n    <div\n      *ngIf=\"_leadingIcon\"\n      class=\"e11-absolute e11-leading-icon e11-left-0 e11-pl-3 e11-flex e11-items-center e11-pointer-events-none\"\n      [innerHTML]=\"_leadingIcon\"\n    ></div>\n    <input *ngIf=\"loading\" class=\"e11-skeleton {{ size }} e11-w-full e11-cursor-wait\" disabled autocomplete=\"off\" />\n    <input\n      #input\n      [type]=\"!loading ? type : 'hidden'\"\n      [name]=\"name\"\n      [id]=\"id\"\n      [placeholder]=\"placeholder\"\n      [formControlName]=\"formControlName\"\n      (blur)=\"onBlur()\"\n      (keyup)=\"onKeyup($any($event.target).value)\"\n      (focus)=\"onFocus()\"\n      (change)=\"handleOnChange($event)\"\n      [required]=\"required\"\n      [value]=\"inputValue\"\n      [max]=\"max\"\n      [readOnly]=\"disabled || readonly\"\n      [attr.maxLength]=\"enforceCharacterLimit ? maxLength : null\"\n      [attr.title]=\"disabled ? disabledText : null\"\n      autocomplete=\"{{ autocomplete }}\"\n      class=\"e11-appearance-none e11-block disabled:e11-opacity-50 disabled:e11-cursor-not-allowed e11-placeholder-gray-400\n        {{ size }} {{ inputClass }}\"\n      [ngClass]=\"{\n        'e11-border-skin-info e11-text-skin-info focus:e11-ring-skin-info focus:e11-border-skin-info e11-pr-10':\n          !hasError && isFocused && reachingMaxLength && !disabled,\n        'e11-border-skin-error e11-text-skin-error focus:e11-ring-skin-error focus:e11-border-skin-error e11-pr-10': hasError,\n        'e11-border-skin-app-borders hover:e11-shadow focus:e11-ring-skin-primary-accent focus:e11-border-skin-primary-accent':\n          !hasError && !darkMode && !disabled,\n        'e11-border-skin-app-bg-dark focus:e11-ring-skin-app-bg-dark focus:e11-border-skin-app-bg-dark': !hasError && darkMode && !disabled,\n        'e11-pl-10': _leadingIcon,\n        'e11-pr-12': togglePassword,\n        'e11-pr-3': type === 'search',\n        'e11-bg-skin-app-bg': disabled,\n        'e11-text-base': size === 'md',\n        'e11-w-full': !hasButton,\n        'e11-float-left': hasButton,\n        'e11-bg-skin-grey e11-text-skin-white e11-placeholder-white': darkMode,\n        'e11-rounded-full': isRounded,\n        'e11-rounded-sm': !isRounded,\n        'placeholder-pad-right e11-pr-10': _trailingIcon,\n        'focus:e11-ring-0 focus:e11-border-skin-app-borders disabled:e11-border-skin-app-borders': disabled\n      }\"\n      aria-invalid=\"true\"\n      aria-describedby=\"email-error\"\n    />\n    <div *ngIf=\"hasError\" class=\"e11-absolute e11-inset-y-0 e11-right-0 e11-pr-3 e11-flex e11-items-center e11-pointer-events-none\">\n      <e11-icon-error></e11-icon-error>\n    </div>\n    <div\n      *ngIf=\"!hasError && _trailingIcon\"\n      class=\"e11-absolute e11-inset-y-0 e11-right-0 e11-pr-3 e11-flex e11-items-center e11-pointer-events-none\"\n      [innerHTML]=\"_trailingIcon\"\n    ></div>\n    <div\n      *ngIf=\"(type === 'password' && togglePassword && !hasError) || (type === 'text' && togglePassword && !hasError)\"\n      class=\"e11-absolute e11-inset-y-0 e11-right-0 e11-pr-3 e11-flex e11-items-center e11-cursor-pointer\"\n      (click)=\"setShowPassword()\"\n    >\n      <span [ngClass]=\"iconType ? 'material-icons-' + iconType : 'material-icons'\" class=\"{{ iconSize }}\">\n        {{ type === 'text' ? 'visibility' : 'visibility_off' }}\n      </span>\n    </div>\n    <!-- Input paired with a button / always on the right / Forcing value to empty string intentionally -->\n    <e11-button\n      *ngIf=\"hasButton\"\n      [paddingX]=\"'sm'\"\n      value=\"\"\n      [icon]=\"buttonIcon\"\n      [iconSize]=\"buttonIconSize\"\n      [iconType]=\"buttonIconType\"\n      [iconPosition]=\"'right'\"\n      type=\"submit\"\n      [size]=\"size\"\n      containerClassOverrides=\"input-button\"\n    ></e11-button>\n  </div>\n  <ng-content></ng-content>\n  <p *ngIf=\"hintText\" class=\"e11-mt-2 e11-text-sm e11-text-skin-base\">{{ hintText }}</p>\n</div>\n", styles: ["@media screen and (max-width: 640px){.e11-input-text input{font-size:16px!important}}::-ms-reveal{display:none}.e11-input-text input[placeholder]{overflow:hidden;text-overflow:ellipsis;white-space:nowrap}.e11-input-text input::placeholder{overflow:hidden;text-overflow:ellipsis;white-space:nowrap}.e11-input-text input:focus::placeholder{overflow:hidden;text-overflow:ellipsis;white-space:nowrap}.e11-input-text input:focus[placeholder]{overflow:hidden;text-overflow:ellipsis;white-space:nowrap}\n"], dependencies: [{ kind: "directive", type: i3.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i3.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i1.DefaultValueAccessor, selector: "input:not([type=checkbox])[formControlName],textarea[formControlName],input:not([type=checkbox])[formControl],textarea[formControl],input:not([type=checkbox])[ngModel],textarea[ngModel],[ngDefaultControl]" }, { kind: "directive", type: i1.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { kind: "directive", type: i1.NgControlStatusGroup, selector: "[formGroupName],[formArrayName],[ngModelGroup],[formGroup],form:not([ngNoForm]),[ngForm]" }, { kind: "directive", type: i1.RequiredValidator, selector: ":not([type=checkbox])[required][formControlName],:not([type=checkbox])[required][formControl],:not([type=checkbox])[required][ngModel]", inputs: ["required"] }, { kind: "directive", type: i1.FormGroupDirective, selector: "[formGroup]", inputs: ["formGroup"], outputs: ["ngSubmit"], exportAs: ["ngForm"] }, { kind: "directive", type: i1.FormControlName, selector: "[formControlName]", inputs: ["formControlName", "disabled", "ngModel"], outputs: ["ngModelChange"] }, { kind: "component", type: i4.E11ButtonComponent, selector: "e11-button", inputs: ["color", "ghost", "fontColor", "id", "name", "type", "value", "size", "style", "buttonStyle", "loading", "skeletonLoading", "loadingText", "display", "buttonGroup", "paddingX", "icon", "iconType", "symbol", "symbolType", "iconSize", "iconPosition", "iconClassOverrides", "containerClassOverrides", "disabledText", "disabled", "titleText", "titleTextPosition", "autofocus"] }, { kind: "component", type: i5.E11InputLabelComponent, selector: "e11-input-label", inputs: ["label", "id", "for", "size", "labelClassOverrides", "required", "hasError", "disabled", "darkMode", "requiredMode", "labelTextOptional", "tooltipConfig"] }, { kind: "component", type: i6.E11IconErrorComponent, selector: "e11-icon-error", inputs: ["classOverrides"] }], encapsulation: i0.ViewEncapsulation.None }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11InputComponent, decorators: [{
            type: Component,
            args: [{ selector: 'e11-input', providers: [
                        {
                            provide: NG_VALUE_ACCESSOR,
                            useExisting: forwardRef(() => E11InputComponent),
                            multi: true,
                        },
                    ], encapsulation: ViewEncapsulation.None, template: "<div class=\"e11-input-text {{ containerClass }}\" [formGroup]=\"parentForm\" (mouseenter)=\"onMouseEnter()\" (mouseleave)=\"onMouseLeave()\">\n  <e11-input-label\n    [for]=\"name\"\n    [label]=\"label\"\n    [required]=\"required\"\n    [disabled]=\"disabled\"\n    [hasError]=\"hasError\"\n    [darkMode]=\"darkMode\"\n    [tooltipConfig]=\"tooltipConfig\"\n  ></e11-input-label>\n  <div class=\"e11-relative e11-flex e11-items-center\">\n    <div\n      *ngIf=\"_leadingIcon\"\n      class=\"e11-absolute e11-leading-icon e11-left-0 e11-pl-3 e11-flex e11-items-center e11-pointer-events-none\"\n      [innerHTML]=\"_leadingIcon\"\n    ></div>\n    <input *ngIf=\"loading\" class=\"e11-skeleton {{ size }} e11-w-full e11-cursor-wait\" disabled autocomplete=\"off\" />\n    <input\n      #input\n      [type]=\"!loading ? type : 'hidden'\"\n      [name]=\"name\"\n      [id]=\"id\"\n      [placeholder]=\"placeholder\"\n      [formControlName]=\"formControlName\"\n      (blur)=\"onBlur()\"\n      (keyup)=\"onKeyup($any($event.target).value)\"\n      (focus)=\"onFocus()\"\n      (change)=\"handleOnChange($event)\"\n      [required]=\"required\"\n      [value]=\"inputValue\"\n      [max]=\"max\"\n      [readOnly]=\"disabled || readonly\"\n      [attr.maxLength]=\"enforceCharacterLimit ? maxLength : null\"\n      [attr.title]=\"disabled ? disabledText : null\"\n      autocomplete=\"{{ autocomplete }}\"\n      class=\"e11-appearance-none e11-block disabled:e11-opacity-50 disabled:e11-cursor-not-allowed e11-placeholder-gray-400\n        {{ size }} {{ inputClass }}\"\n      [ngClass]=\"{\n        'e11-border-skin-info e11-text-skin-info focus:e11-ring-skin-info focus:e11-border-skin-info e11-pr-10':\n          !hasError && isFocused && reachingMaxLength && !disabled,\n        'e11-border-skin-error e11-text-skin-error focus:e11-ring-skin-error focus:e11-border-skin-error e11-pr-10': hasError,\n        'e11-border-skin-app-borders hover:e11-shadow focus:e11-ring-skin-primary-accent focus:e11-border-skin-primary-accent':\n          !hasError && !darkMode && !disabled,\n        'e11-border-skin-app-bg-dark focus:e11-ring-skin-app-bg-dark focus:e11-border-skin-app-bg-dark': !hasError && darkMode && !disabled,\n        'e11-pl-10': _leadingIcon,\n        'e11-pr-12': togglePassword,\n        'e11-pr-3': type === 'search',\n        'e11-bg-skin-app-bg': disabled,\n        'e11-text-base': size === 'md',\n        'e11-w-full': !hasButton,\n        'e11-float-left': hasButton,\n        'e11-bg-skin-grey e11-text-skin-white e11-placeholder-white': darkMode,\n        'e11-rounded-full': isRounded,\n        'e11-rounded-sm': !isRounded,\n        'placeholder-pad-right e11-pr-10': _trailingIcon,\n        'focus:e11-ring-0 focus:e11-border-skin-app-borders disabled:e11-border-skin-app-borders': disabled\n      }\"\n      aria-invalid=\"true\"\n      aria-describedby=\"email-error\"\n    />\n    <div *ngIf=\"hasError\" class=\"e11-absolute e11-inset-y-0 e11-right-0 e11-pr-3 e11-flex e11-items-center e11-pointer-events-none\">\n      <e11-icon-error></e11-icon-error>\n    </div>\n    <div\n      *ngIf=\"!hasError && _trailingIcon\"\n      class=\"e11-absolute e11-inset-y-0 e11-right-0 e11-pr-3 e11-flex e11-items-center e11-pointer-events-none\"\n      [innerHTML]=\"_trailingIcon\"\n    ></div>\n    <div\n      *ngIf=\"(type === 'password' && togglePassword && !hasError) || (type === 'text' && togglePassword && !hasError)\"\n      class=\"e11-absolute e11-inset-y-0 e11-right-0 e11-pr-3 e11-flex e11-items-center e11-cursor-pointer\"\n      (click)=\"setShowPassword()\"\n    >\n      <span [ngClass]=\"iconType ? 'material-icons-' + iconType : 'material-icons'\" class=\"{{ iconSize }}\">\n        {{ type === 'text' ? 'visibility' : 'visibility_off' }}\n      </span>\n    </div>\n    <!-- Input paired with a button / always on the right / Forcing value to empty string intentionally -->\n    <e11-button\n      *ngIf=\"hasButton\"\n      [paddingX]=\"'sm'\"\n      value=\"\"\n      [icon]=\"buttonIcon\"\n      [iconSize]=\"buttonIconSize\"\n      [iconType]=\"buttonIconType\"\n      [iconPosition]=\"'right'\"\n      type=\"submit\"\n      [size]=\"size\"\n      containerClassOverrides=\"input-button\"\n    ></e11-button>\n  </div>\n  <ng-content></ng-content>\n  <p *ngIf=\"hintText\" class=\"e11-mt-2 e11-text-sm e11-text-skin-base\">{{ hintText }}</p>\n</div>\n", styles: ["@media screen and (max-width: 640px){.e11-input-text input{font-size:16px!important}}::-ms-reveal{display:none}.e11-input-text input[placeholder]{overflow:hidden;text-overflow:ellipsis;white-space:nowrap}.e11-input-text input::placeholder{overflow:hidden;text-overflow:ellipsis;white-space:nowrap}.e11-input-text input:focus::placeholder{overflow:hidden;text-overflow:ellipsis;white-space:nowrap}.e11-input-text input:focus[placeholder]{overflow:hidden;text-overflow:ellipsis;white-space:nowrap}\n"] }]
        }], ctorParameters: () => [{ type: i1.ControlContainer, decorators: [{
                    type: Optional
                }, {
                    type: Host
                }, {
                    type: SkipSelf
                }] }, { type: i0.ElementRef }, { type: i2.DomSanitizer }, { type: i0.ChangeDetectorRef }, { type: undefined, decorators: [{
                    type: Optional
                }, {
                    type: Inject,
                    args: [INPUT_CONFIG]
                }] }], propDecorators: { containerClass: [{
                type: Input
            }], inputClass: [{
                type: Input
            }], hintText: [{
                type: Input
            }], label: [{
                type: Input
            }], type: [{
                type: Input
            }], size: [{
                type: Input
            }], style: [{
                type: Input
            }], placeholder: [{
                type: Input
            }], maxLength: [{
                type: Input
            }], max: [{
                type: Input
            }], name: [{
                type: Input
            }], id: [{
                type: Input
            }], iconLeft: [{
                type: Input
            }], iconLeftBorder: [{
                type: Input
            }], required: [{
                type: Input
            }], hasError: [{
                type: Input
            }], labelColor: [{
                type: Input
            }], borderGlow: [{
                type: Input
            }], checkIcon: [{
                type: Input
            }], clearIcon: [{
                type: Input
            }], autofocus: [{
                type: Input
            }], togglePassword: [{
                type: Input
            }], hasButton: [{
                type: Input
            }], buttonText: [{
                type: Input
            }], buttonIcon: [{
                type: Input
            }], buttonIconType: [{
                type: Input
            }], buttonIconSize: [{
                type: Input
            }], readonly: [{
                type: Input
            }], disabled: [{
                type: Input
            }], disabledText: [{
                type: Input
            }], autocomplete: [{
                type: Input
            }], formControlName: [{
                type: Input
            }], isRounded: [{
                type: Input
            }], parentForm: [{
                type: Input
            }], tooltipConfig: [{
                type: Input
            }], icon: [{
                type: Input
            }], iconType: [{
                type: Input
            }], iconSize: [{
                type: Input
            }], darkMode: [{
                type: Input
            }], context: [{
                type: Input
            }], enforceCharacterLimit: [{
                type: Input
            }], showCharacterLimitWarning: [{
                type: Input
            }], loading: [{
                type: Input
            }], change: [{
                type: Output,
                args: ['change']
            }], focus: [{
                type: Output,
                args: ['focus']
            }], blur: [{
                type: Output,
                args: ['blur']
            }], inputElement: [{
                type: ViewChild,
                args: ['input', { static: false }]
            }], leadingIcon: [{
                type: Input
            }], trailingIcon: [{
                type: Input
            }] } });

class E11InputModule {
    static forRoot(config = DEFAULT_CONFIG) {
        return {
            ngModule: E11InputModule,
            providers: [{ provide: INPUT_CONFIG, useValue: { ...DEFAULT_CONFIG, ...config } }],
        };
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11InputModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.2.13", ngImport: i0, type: E11InputModule, declarations: [E11InputComponent], imports: [CommonModule, ReactiveFormsModule, E11ButtonModule, E11InputLabelModule, E11IconModule, E11TooltipModule, E11UtilModule], exports: [E11InputComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11InputModule, imports: [CommonModule, ReactiveFormsModule, E11ButtonModule, E11InputLabelModule, E11IconModule, E11TooltipModule, E11UtilModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11InputModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [E11InputComponent],
                    imports: [CommonModule, ReactiveFormsModule, E11ButtonModule, E11InputLabelModule, E11IconModule, E11TooltipModule, E11UtilModule],
                    exports: [E11InputComponent],
                    providers: [],
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { DEFAULT_CONFIG, E11InputComponent, E11InputModule, INPUT_CONFIG };
